import { RequestController } from '../request/controller';
import { CacheController } from '../cache/controller';
import { PaginationMeta } from '../../types';
import { User } from '../../types/user';
import { EnvModel } from '../env/model';

export class API {
  constructor(protected request: RequestController, protected cache: CacheController, protected env: EnvModel) {}
}

export class APIError extends Error {
  code: number;
  reason: string;
  isServerError: boolean;
  isClientError: boolean;
  isNetworkError: boolean;

  constructor(httpError: number, message: string) {
    super(`HTTP ${httpError}: ${message}`);
    this.name = 'APIError';
    this.code = httpError;
    this.reason = message;
    this.isServerError = httpError >= 500 && httpError <= 599;
    this.isClientError = httpError >= 400 && httpError <= 499;
    this.isNetworkError = httpError === -1;
    if (typeof Error.captureStackTrace === 'function') {
      // Maintains proper stack trace for where our error was thrown (only available on V8)
      Error.captureStackTrace(this, APIError);
    }
  }

  static async checkResponse(response: Response) {
    if (!response.ok) {
      let message = response.statusText;
      try {
        const json = await response.json();
        if (typeof json.error === 'string') {
          message = json.error;
        }
      } catch {
        // Likely a JSON parsing error, fallback to HTTP status text
      }
      throw new APIError(response.status, message);
    }
  }
}

export interface APIResponse<T> {
  data: T;
  status?: boolean;
  success?: boolean;
  error: string | { [key: string]: string };
}

export interface APIPaginatedResponse<T> {
  data: T;
  meta: {
    pagination: PaginationMeta;
  };
}

export const getEmptyPaginatedResponse = <P>(data: P[] = []): APIPaginatedResponse<P[]> => ({
  data,
  meta: { pagination: { count: 0, current_page: 1, per_page: 25, total: 0, total_pages: 1 } },
});

export interface UserStatusResponse {
  status: boolean;
  user?: User;
}
